import { useState, useEffect } from 'react'
import { Spinner, Button } from '@heroui/react'
import { Users, Percent, DollarSign, ShoppingCart, GitBranch } from 'lucide-react'
import { funnelApi } from '../api'
import type { AnalyticsSummary } from '../types'
import { MetricCard } from '../components/analytics/MetricCard'
import { FunnelsOverview, RecentAlerts, RecentChanges } from '../components/dashboard'

export default function Dashboard() {
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAnalytics()
  }, [])

  async function loadAnalytics() {
    try {
      setLoading(true)
      setError(null)
      const response = await funnelApi.getAnalytics()
      setData(response.data)
    } catch (err) {
      console.error('Failed to load analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-red-500 mb-2">
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-tf-text-muted font-medium mb-4">
          {error || 'Failed to load analytics'}
        </p>
        <Button color="primary" onPress={loadAnalytics}>
          Retry
        </Button>
      </div>
    )
  }

  // Calculate mock visitors from impressions
  const visitors = data.metrics.impressions

  // Format revenue as currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Calculate AOV from revenue and conversions
  const avgOrderValue = data.metrics.conversions > 0
    ? data.metrics.revenue / data.metrics.conversions
    : 0

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-tf-text">Funnels Dashboard</h1>
        <p className="text-tf-text-muted">Monitor your funnel performance at a glance</p>
      </div>

      {/* KPI Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Visitors"
          value={visitors.toLocaleString()}
          icon={<Users className="w-6 h-6" />}
          trend={{ value: 12.5, isPositive: true }}
          color="var(--fn-primary)"
        />
        <MetricCard
          title="Conversion Rate"
          value={`${data.metrics.conversionRate.toFixed(1)}%`}
          icon={<Percent className="w-6 h-6" />}
          trend={{ value: 3.2, isPositive: true }}
          color="var(--fn-secondary)"
        />
        <MetricCard
          title="Revenue"
          value={formatCurrency(data.metrics.revenue)}
          icon={<DollarSign className="w-6 h-6" />}
          trend={{ value: 8.7, isPositive: true }}
          color="#10b981"
        />
        <MetricCard
          title="AOV"
          value={formatCurrency(avgOrderValue)}
          icon={<ShoppingCart className="w-6 h-6" />}
          trend={{ value: 2.1, isPositive: false }}
          color="#f59e0b"
        />
        <MetricCard
          title="Active Funnels"
          value={data.activeFunnels}
          icon={<GitBranch className="w-6 h-6" />}
          trend={{ value: 5.0, isPositive: true }}
          color="#8b5cf6"
        />
      </div>

      {/* Funnels Overview + Recent Alerts (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FunnelsOverview />
        <RecentAlerts />
      </div>

      {/* Recent Changes (full width) */}
      <RecentChanges />
    </div>
  )
}
